import ScratchJr from '../../../ScratchJr';
import ActivityStorageService from '../../storage/ActivityStorageService';
import ActivityAudio from '../../shared/ActivityAudio';
import {scorePreferredNarrationVoice} from '../../shared/SpeechVoicePreference';
import GoalMarker from './GoalMarker';
import PaletteRestrictor from '../guided-creation/PaletteRestrictor';
import Palette from '../../../ui/Palette';
import Thumbs from '../../../ui/Thumbs';
import {gn} from '../../../../utils/lib';

const STAGE_WIDTH = 480;
const STAGE_HEIGHT = 360;
const DEFAULT_PHASE = 'INTRO';
const PHASES = Object.freeze({
    INTRO: 'INTRO',
    TASK_1: 'TASK_1',
    TASK_2: 'TASK_2',
    OBSTACLE_INTRO: 'OBSTACLE_INTRO',
    TASK_3: 'TASK_3',
    CELEBRATION: 'CELEBRATION',
    BONUS: 'BONUS',
    COMPLETE: 'COMPLETE'
});

export default class StoryWalkFlow {
    constructor (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;
        this._stage = container.querySelector('.stage') || container;
        this._goalMarker = null;
        this._paletteRestrictor = null;
        this._overlay = null;
        this._pond = null;
        this._goalSparkles = null;
        this._doneBtn = null;
        this._toast = null;
        this._hintArrow = null;
        this._ghostHand = null;
        this._uiRepairTimer = null;
        this._uiRepairTimers = [];
        this._interactionSyncTimers = [];
        this._followUpTimers = [];
        this._phase = DEFAULT_PHASE;
        this._runMonitor = null;
        this._scriptMonitor = null;
        this._idleTimers = [];
        this._phaseTimeout = null;
        this._coachTimeout = null;
        this._lastNarrationId = null;
        this._lastNarrationText = '';
        this._lastInteractionAt = Date.now();
        this._idlePausedAt = null;
        this._idleTimeMs = 0;
        this._sessionStartedAt = Date.now();
        this._phaseStartedAt = Date.now();
        this._lastBlockSignature = '';
        this._hasScriptChanged = false;
        this._runStartPosition = null;
        this._collisionOnRun = false;
        this._wentOffScreen = false;
        this._pondWarningShown = false;
        this._hintEvents = [];
        this._pondCollisions = 0;
        this._speechVoice = null;
        this._speechVoiceScore = 0;
        this._lastVoiceName = '';
        this._lastVoiceLang = '';
        this._lastPaletteCategory = null;
        this._voicesChangeHandler = this._refreshPreferredVoice.bind(this);
        this._lastSpokenAt = 0;
        this._lastCoachAt = 0;
        this._awaitingHintResponse = false;
        this._skillProfile = null;
        this._voiceHistory = [];
        this._milestoneAnnouncements = {
            flagPlaced: false,
            movePlaced: false,
            endPlaced: false,
            sequenceBuilt: false,
            verticalChoice: false,
            debugging: false,
            hintResponse: false
        };
        this._outcomeFlags = {
            outcome_1: false,
            outcome_2: false,
            outcome_3: false,
            outcome_4: false,
            outcome_5: false
        };
        this._speechQueue = [];
        this._isSpeaking = false;
        this._onPageHide = this._handlePageHide.bind(this);
        this._onVisibilityChange = this._handleVisibilityChange.bind(this);
        this._onPointerDown = this._handleMeaningfulInteraction.bind(this);
        this._onPointerUp = this._scheduleDeferredInteractionChecks.bind(this);
        this._onPointerUpReplay = this._replayCurrentNarration.bind(this);
        this._config = this._buildConfig(activityData);
    }

    async init () {
        this._renderChrome();
        this._initVoiceSelection();
        window.addEventListener('pagehide', this._onPageHide);
        document.addEventListener('visibilitychange', this._onVisibilityChange);
        await this._waitForPreferredVoice();
        this._goalMarker = new GoalMarker(
            this._stage,
            this._config.goal.x,
            this._config.goal.y,
            this._config.goal.tolerance
        );
        this._goalMarker.show();

        this._paletteRestrictor = new PaletteRestrictor(this._container, []);
        this._attachListeners();
        this._applySpriteRoles();
        this._forcePlayerSelection();
        this._stabilizeChildFacingUi();

        const saved = await ActivityStorageService.getActivityProgress(this._activityData.activityId);
        const savedPhase = saved && saved.currentState;
        if (saved && saved.outcomeFlags) {
            this._outcomeFlags = {...this._outcomeFlags, ...saved.outcomeFlags};
        }
        if (saved && Array.isArray(saved.hintEvents)) {
            this._hintEvents = saved.hintEvents.slice();
        }
        if (saved && typeof saved.pondCollisions === 'number') {
            this._pondCollisions = saved.pondCollisions;
        }
        if (saved && typeof saved.sessionStartedAt === 'number') {
            this._sessionStartedAt = saved.sessionStartedAt;
        }
        if (saved && typeof saved.idleTimeMs === 'number') {
            this._idleTimeMs = saved.idleTimeMs;
        }

        this._skillProfile = await this._buildSkillProfile(saved, savedPhase);

        if (savedPhase && PHASES[savedPhase]) {
            // If saved in INTRO or early TASK_1 with no blocks, replay the full intro
            if (savedPhase === PHASES.INTRO || (savedPhase === PHASES.TASK_1 && !saved.scriptSnapshot)) {
                this._setPhase(PHASES.INTRO, {silent: true});
                this._syncVisualState();
                this._playIntroSequence();
                await this._persistProgress();
                return;
            }
            // CELEBRATION is transient — resume directly into BONUS (no Done button)
            if (savedPhase === PHASES.CELEBRATION) {
                this._setPhase(PHASES.BONUS, {silent: true});
                this._syncVisualState();
                this._startIdleScaffolding();
                this._speak('VO-11', 'Now try the green sound block. You can even record your own voice!');
                return;
            }
            this._setPhase(savedPhase, {silent: true});
            // No Done button — activity auto-completes when child uses sound block
            this._syncVisualState();
            this._startIdleScaffolding();
            this._speak('VO-02', this._buildTaskPrompt(savedPhase, {isResume: true}));
            return;
        }

        this._setPhase(PHASES.INTRO, {silent: true});
        this._syncVisualState();
        this._playIntroSequence();
        await this._persistProgress();
    }

    onStateChange (prevState, newState) {
        if (prevState === 'SUCCESS' && newState === 'BUILDING') {
            this._resetForReplay();
            return;
        }

        if (newState === 'RUNNING') {
            this._clearIdleTimers();
            this._runStartPosition = this._getPlayerPosition();
            this._collisionOnRun = false;
            this._wentOffScreen = false;
            this._pondWarningShown = false;
            this._startRunMonitor();
            return;
        }

        if (newState === 'VALIDATING') {
            this._stopRunMonitor();
            this._validateRun();
            return;
        }

        if (newState === 'BUILDING' || newState === 'READY') {
            this._stopRunMonitor();
            this._syncVisualState();
            this._startIdleScaffolding();
        }
    }

    getBlockCount () {
        return ScratchJr.collectActivityBlocks().length;
    }

    evaluate () {
        return {
            success: this._phase === PHASES.COMPLETE,
            usedBlocks: this.getBlockCount(),
            storyPhase: this._phase
        };
    }

    showHint () {
        this._replayCurrentNarration();
    }

    captureRunStartPosition () {
        this._runStartPosition = this._getPlayerPosition();
    }

    restartActivity () {
        if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
            this._stateMachine.transition('BUILDING');
        }
        this._resetForReplay();
        return true;
    }

    _handlePageHide () {
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            window.speechSynthesis.cancel();
        }
    }

    _handleVisibilityChange () {
        if (document.hidden && window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            window.speechSynthesis.cancel();
        }
    }

    destroy () {
        this._clearIdleTimers();
        this._stopRunMonitor();
        this._stopScriptMonitor();
        this._clearCoachTimeout();
        this._clearInteractionSyncTimers();
        this._clearFollowUpTimers();
        this._clearUiRepair();
        this._clearUiRepairs();
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
        this._dismissCompletionModal();
        if (this._goalMarker) {
            this._goalMarker.destroy();
            this._goalMarker = null;
        }
        if (this._paletteRestrictor) {
            this._paletteRestrictor.destroy();
            this._paletteRestrictor = null;
        }
        if (this._toast && this._toast.parentNode) {
            this._toast.parentNode.removeChild(this._toast);
        }
        if (this._overlay && this._overlay.parentNode) {
            this._overlay.parentNode.removeChild(this._overlay);
        }
        this._removeReplayListener();
        this._container.removeEventListener('pointerdown', this._onPointerDown, true);
        this._container.removeEventListener('pointerup', this._onPointerUp, true);
        window.removeEventListener('pagehide', this._onPageHide);
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        this._speechQueue = [];
        this._isSpeaking = false;
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            window.speechSynthesis.cancel();
        }
        if (window.speechSynthesis && typeof window.speechSynthesis.removeEventListener === 'function') {
            window.speechSynthesis.removeEventListener('voiceschanged', this._voicesChangeHandler);
        }
    }

    _buildConfig (activityData) {
        const storyWalk = activityData.storyWalk || {};
        const firstPage = activityData.projectTemplate && activityData.projectTemplate.pages
            ? activityData.projectTemplate.pages[0]
            : {};
        const sprites = Array.isArray(firstPage.sprites) ? firstPage.sprites : [];
        const playerIndex = Math.max(0, sprites.findIndex((sprite) => sprite.role === 'player'));
        const goalIndex = sprites.findIndex((sprite) => sprite.role === 'goal');
        const player = sprites[playerIndex] || sprites[0] || {name: 'Bluux', x: 60, y: 180};
        const goal = sprites[goalIndex > -1 ? goalIndex : 1] || {name: 'Dog', x: 420, y: 180};

        return {
            playerIndex,
            goalIndex: goalIndex > -1 ? goalIndex : 1,
            introDelayMs: storyWalk.introDelayMs || 4200,
            introPauseMs: storyWalk.introPauseMs || 1000,
            midpointX: storyWalk.midpointX || 204,
            task1MinX: storyWalk.task1MinX || ((player.x || 60) + 42),
            goal: {
                x: (activityData.successCriteria && activityData.successCriteria.goalX) || goal.x || 420,
                y: (activityData.successCriteria && activityData.successCriteria.goalY) || goal.y || 180,
                tolerance: (activityData.successCriteria && activityData.successCriteria.tolerance) || 40,
                coverageThreshold: (activityData.successCriteria && activityData.successCriteria.goalCoverageThreshold) || 0.8,
                hitboxScale: (activityData.successCriteria && activityData.successCriteria.goalHitboxScale) || 0.55
            },
            pond: storyWalk.pond || {
                x: 220,
                y: 150,
                width: 104,
                height: 72,
                asset: '/assets/ui/activity-storywalk-pond.svg'
            },
            availableBlocks: storyWalk.availableBlocks || {
                [PHASES.TASK_1]: ['onflag', 'forward', 'endstack'],
                [PHASES.TASK_2]: ['onflag', 'forward', 'say', 'endstack'],
                [PHASES.OBSTACLE_INTRO]: ['onflag', 'forward', 'back', 'up', 'down', 'endstack'],
                [PHASES.TASK_3]: ['onflag', 'forward', 'back', 'up', 'down', 'endstack'],
                [PHASES.CELEBRATION]: ['onflag', 'forward', 'back', 'up', 'down', 'endstack'],
                [PHASES.BONUS]: ['onflag', 'forward', 'back', 'up', 'down', 'say', 'playsnd', 'playusersnd', 'endstack']
            },
            voiceOver: storyWalk.voiceOver || {},
            introAdvancePhase: storyWalk.introAdvancePhase || PHASES.TASK_1,
            restartPhase: storyWalk.restartPhase || storyWalk.introAdvancePhase || PHASES.TASK_1,
            persistPositionBetweenRuns: Boolean(storyWalk.persistPositionBetweenRuns),
            voiceConfidenceThreshold: storyWalk.voiceConfidenceThreshold || 90,
            idleScaffolding: storyWalk.idleScaffolding || {
                [PHASES.TASK_1]: [15000, 30000, 45000, 60000, 180000, 240000],
                [PHASES.TASK_2]: [15000, 30000, 45000, 60000, 180000, 240000],
                [PHASES.TASK_3]: [20000, 40000, 60000, 80000, 200000, 260000],
                [PHASES.CELEBRATION]: [],
                [PHASES.BONUS]: []
            }
        };
    }

    _renderChrome () {
        this._overlay = document.createElement('div');
        this._overlay.className = 'activity-storywalk-layer';

        this._goalSparkles = document.createElement('div');
        this._goalSparkles.className = 'activity-storywalk-goal-sparkles';
        this._overlay.appendChild(this._goalSparkles);

        this._pond = document.createElement('div');
        this._pond.className = 'activity-storywalk-pond is-hidden';
        this._pond.style.left = `${this._config.pond.x}px`;
        this._pond.style.top = `${this._config.pond.y}px`;
        this._pond.style.width = `${this._config.pond.width}px`;
        this._pond.style.height = `${this._config.pond.height}px`;
        if (this._config.pond.asset) {
            this._pond.style.backgroundImage = `url('${this._config.pond.asset}')`;
        }
        this._overlay.appendChild(this._pond);

        this._stage.appendChild(this._overlay);
    }

    _attachListeners () {
        this._container.addEventListener('pointerdown', this._onPointerDown, true);
        this._container.addEventListener('pointerup', this._onPointerUp, true);
        this._attachReplayListener();
        this._lastBlockSignature = this._getBlockSignature();
        this._startScriptMonitor();
    }

    _attachReplayListener () {
        this._removeReplayListener();
        const playerEl = this._getPlayerSpriteElement();
        if (playerEl) {
            playerEl.addEventListener('pointerup', this._onPointerUpReplay);
        }
    }

    _removeReplayListener () {
        const playerEl = this._getPlayerSpriteElement();
        if (playerEl) {
            playerEl.removeEventListener('pointerup', this._onPointerUpReplay);
        }
    }

    _handleMeaningfulInteraction () {
        this._processMeaningfulInteraction();
        this._scheduleDeferredInteractionChecks();
    }

    _processMeaningfulInteraction () {
        this._clearCoachTimeout();
        const previousSignature = this._lastBlockSignature;
        const nextSignature = this._getBlockSignature();
        const scriptChanged = nextSignature !== previousSignature;

        if (previousSignature && nextSignature && scriptChanged) {
            this._hasScriptChanged = true;
            this._outcomeFlags.outcome_3 = true;
        }
        this._lastBlockSignature = nextSignature;

        if (scriptChanged) {
            if (this._phase !== PHASES.INTRO) {
                this._maybeCelebrateProgress(previousSignature, nextSignature);
            }
            this._syncVisualState();
        }

        if (this._idlePausedAt) {
            this._idleTimeMs += Math.max(0, Date.now() - this._idlePausedAt);
            this._idlePausedAt = null;
        }

        this._lastInteractionAt = Date.now();
        this._clearIdleTimers();
        this._clearHintVisuals();
        this._startIdleScaffolding();
        this._persistProgress();
    }

    _scheduleDeferredInteractionChecks () {
        this._clearInteractionSyncTimers();
        [60, 180, 420, 900].forEach((delayMs) => {
            const timer = setTimeout(() => {
                this._processMeaningfulInteraction();
            }, delayMs);
            this._interactionSyncTimers.push(timer);
        });
    }

    _clearInteractionSyncTimers () {
        while (this._interactionSyncTimers.length > 0) {
            clearTimeout(this._interactionSyncTimers.pop());
        }
    }

    _scheduleFollowUp (delayMs, callback) {
        const timer = setTimeout(() => {
            this._followUpTimers = this._followUpTimers.filter((activeTimer) => activeTimer !== timer);
            callback();
        }, delayMs);
        this._followUpTimers.push(timer);
    }

    _clearFollowUpTimers () {
        while (this._followUpTimers.length > 0) {
            clearTimeout(this._followUpTimers.pop());
        }
    }

    _startScriptMonitor () {
        this._stopScriptMonitor();
        this._scriptMonitor = setInterval(() => {
            const nextSignature = this._getBlockSignature();
            if (nextSignature !== this._lastBlockSignature) {
                this._processMeaningfulInteraction();
            }
        }, 220);
    }

    _stopScriptMonitor () {
        if (this._scriptMonitor) {
            clearInterval(this._scriptMonitor);
            this._scriptMonitor = null;
        }
    }

    _validateRun () {
        if (this._phase === PHASES.INTRO || this._phase === PHASES.OBSTACLE_INTRO || this._phase === PHASES.CELEBRATION) {
            this._stateMachine.transition('BUILDING');
            return;
        }

        const blocks = ScratchJr.collectActivityBlocks();
        const player = this._getPlayerSpriteSnapshot();
        const goalFit = this._evaluateGoalFit(player);
        const reachedGoal = goalFit.success;
        const reachedMidpoint = player.x >= this._config.midpointX;
        const movedRight = player.x >= this._config.task1MinX;

        this._lastBlockSignature = this._getBlockSignature();

        // Goal check FIRST — if child reached Dog, always celebrate regardless of edge proximity
        // Note: TASK_2 is NOT here — it requires say block, not reaching Dog
        if (reachedGoal) {
            if (this._phase === PHASES.TASK_3) {
                this._outcomeFlags.outcome_4 = true;
                this._outcomeFlags.outcome_5 = true;
                this._advanceToCelebration();
                return;
            }
            if (this._phase === PHASES.TASK_1 && this._scriptHas('onflag') && this._scriptHas('forward') && this._scriptHas('endstack')) {
                this._outcomeFlags.outcome_1 = true;
                this._outcomeFlags.outcome_4 = true;
                this._advanceToCelebration();
                return;
            }
        }

        // Off-screen detection — uses flag set by run monitor before clamping
        if (this._wentOffScreen) {
            this._wentOffScreen = false;
            this._speak('VO-OFFSCREEN', "Whoa, that's the wrong way! Dog is over there!");
            this._stateMachine.transition('BUILDING');
            return;
        }

        // Collision check — only after goal check for TASK_3 so pond bypass paths that
        // still reach Dog are celebrated rather than reset
        if (this._collisionOnRun) {
            this._pondCollisions++;
            this._showPlayerBubble('Splash!', 2200);
            this._speak('VO-08');
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (this._phase === PHASES.TASK_1) {
            if (movedRight && this._scriptHas('onflag') && this._scriptHas('forward') && this._scriptHas('endstack')) {
                this._outcomeFlags.outcome_1 = true;
                this._showPlayerBubble('I moved!', 2500);
                ActivityAudio.play('star-chime');
                this._speak('VO-04');
                this._advanceToTask2();
                return;
            }
            this._stateMachine.transition('BUILDING');
            return;
        }

        if (this._phase === PHASES.TASK_2) {
            // Task 2 is "make them talk" with speech bubbles (say block only)
            if (this._scriptHas('say')) {
                this._outcomeFlags.outcome_2 = true;
                this._showPlayerBubble('We are talking!', 2500);
                this._showGoalBubble('WOOF WOOF!', 3000);
                ActivityAudio.play('star-chime');
                this._speak('VO-05', 'They are talking! Now let us try something harder.');
                this._advanceToObstacleIntro();
                return;
            }
            this._stateMachine.transition('BUILDING');
            return;
        }

        if (this._phase === PHASES.TASK_3) {
            // Goal already checked above; if we get here, goal was not reached
            this._stateMachine.transition('BUILDING');
            return;
        }

        if (this._phase === PHASES.BONUS) {
            const hasSoundBlock = this._scriptHas('playsnd') || this._scriptHas('playusersnd');
            if (hasSoundBlock) {
                this._showPlayerBubble('Try the next activity!', 5000);
                this._speakFunny('VO-12', "You're a programmer now! Try the next activity!");
                setTimeout(() => this._completeBonus(), 5000);
                return;
            }
            this._stateMachine.transition('BUILDING');
        }
    }

    _advanceToTask1 () {
        this._setPhase(PHASES.TASK_1);
        if (!this._scriptHas('onflag')) {
            this._selectPaletteCategory(0);
            this._speak('VO-02', this._buildTaskPrompt(PHASES.TASK_1));
            return;
        }

        if (!this._milestoneAnnouncements.flagPlaced) {
            this._announceCoach('flagPlaced', 'VO-13', {
                afterMs: 450,
                afterSpeak: () => {
                    this._advanceToTask1();
                }
            });
            return;
        }

        if (!this._scriptHas('forward')) {
            this._selectPaletteCategory(1);
            this._highlightHintTarget();
            this._speak('VO-19');
            return;
        }

        if (!this._milestoneAnnouncements.movePlaced) {
            this._showPlayerBubble('Great job!', 2500);
            this._announceCoach('movePlaced', 'VO-14', {
                afterMs: 450,
                afterSpeak: () => {
                    // Guide to end block next
                    this._speak('VO-END', 'Now find the red end block. It tells Blooks the program is finished. Drag it to the end of your blocks.');
                }
            });
            return;
        }

        if (!this._milestoneAnnouncements.endPlaced && !this._scriptHas('endstack')) {
            // Highlight the end block
            this._highlightHintTarget();
            this._speak('VO-END-HINT', 'Find the red end block and connect it at the end. It tells Blooks when to stop.');
            return;
        }

        if (!this._milestoneAnnouncements.endPlaced && this._scriptHas('endstack')) {
            this._showPlayerBubble('All done!', 2000);
            this._announceCoach('endPlaced', 'VO-END-DONE', {
                afterMs: 300,
                afterSpeak: () => {
                    this._highlightHintTarget();
                    this._speak('VO-20', this._buildRunPrompt());
                }
            });
            return;
        }

        this._highlightHintTarget();
        this._speak('VO-20', this._buildRunPrompt());
    }

    _advanceFromIntro () {
        const nextPhase = this._config.introAdvancePhase || PHASES.TASK_1;
        if (nextPhase === PHASES.TASK_3) {
            this._setPhase(PHASES.TASK_3);
            this._revealPond();
            this._speak('VO-02', this._buildTaskPrompt(PHASES.TASK_3));
            this._stateMachine.transition('BUILDING');
            return;
        }

        if (nextPhase === PHASES.TASK_2) {
            this._setPhase(PHASES.TASK_2);
            this._speak('VO-02', this._buildTaskPrompt(PHASES.TASK_2));
            this._stateMachine.transition('BUILDING');
            return;
        }

        this._advanceToTask1();
    }

    _advanceToTask2 () {
        this._setPhase(PHASES.TASK_2);
        // Separate sprites and face Dog toward Penguin for "make them talk"
        this._separateSpritesForTalk();
        this._faceGoalTowardPlayer();
        this._persistProgress();
        this._stateMachine.transition('BUILDING');
        this._scheduleFollowUp(900, () => {
            if (this._phase === PHASES.TASK_2) {
                this._speak('VO-TALK', 'Now make them talk! Try the purple say block to add a speech bubble.');
            }
        });
    }

    _separateSpritesForTalk () {
        const player = this._getPlayerSpriteOwner();
        const goal = this._getGoalSpriteOwner();
        // Place player on left third, goal on right third, both at vertical center
        if (player) {
            player.setPos(120, STAGE_HEIGHT / 2);
        }
        if (goal) {
            goal.setPos(340, STAGE_HEIGHT / 2);
        }
    }

    _advanceToObstacleIntro () {
        this._setPhase(PHASES.OBSTACLE_INTRO);
        this._revealPond();
        this._persistProgress();
        this._phaseTimeout = setTimeout(() => {
            this._repositionSpritesAroundPond();
            this._setPhase(PHASES.TASK_3);
            this._speak('VO-06');
            this._phaseTimeout = setTimeout(() => {
                this._speak('VO-07');
            }, 1800);
            this._stateMachine.transition('BUILDING');
        }, 600);
    }

    _advanceToCelebration () {
        this._setPhase(PHASES.CELEBRATION);
        this._faceGoalTowardPlayer();
        this._launchCelebration();
        this._showPlayerBubble('You did it!');
        const narrationText = this._config.voiceOver['VO-10'] || this._defaultVoiceLine('VO-10');
        const estimatedMs = this._estimateSpeechDurationMs(narrationText) + 2000;
        let advanced = false;
        const advanceOnce = () => {
            if (advanced) {
                return;
            }
            advanced = true;
            this._closePlayerBubble();
            this._advanceToBonus();
        };
        this._speak('VO-10', null, {
            onend: () => {
                if (advanced) {
                    return;
                }
                clearTimeout(this._phaseTimeout);
                this._phaseTimeout = setTimeout(advanceOnce, 2000);
            }
        });
        this._phaseTimeout = setTimeout(advanceOnce, estimatedMs);
        this._persistProgress();
        this._stateMachine.transition('BUILDING');
    }

    _advanceToBonus () {
        this._setPhase(PHASES.BONUS);
        this._showPlayerBubble('Try recording a sound!', 4000);
        this._speak('VO-11', 'Now try the green sound block. You can even record your own voice!');
        this._persistProgress();
        this._stateMachine.transition('BUILDING');
        // Fallback: show Done button after 60s idle in BONUS
        this._phaseTimeout = setTimeout(() => {
            if (this._phase === PHASES.BONUS) {
                this._showDoneButton();
            }
        }, 60000);
    }

    _completeBonus () {
        this._setPhase(PHASES.COMPLETE);
        this._hideDoneButton();
        this._closePlayerBubble();
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            window.speechSynthesis.cancel();
        }
        this._persistProgress({
            status: 'completed',
            completedAt: new Date().toISOString()
        });
        // Show our own completion modal — do NOT transition to SUCCESS
        // (SUCCESS triggers the old FeedbackModal in ActivityUI)
        this._showCompletionModal();
    }

    _showCompletionModal () {
        // Dimmed backdrop
        this._completionOverlay = document.createElement('div');
        this._completionOverlay.className = 'activity-completion-overlay';

        // Modal card
        const modal = document.createElement('div');
        modal.className = 'activity-completion-modal';

        // Close button (red circle X — matches closeit.svg pattern)
        const closeBtn = document.createElement('button');
        closeBtn.className = 'activity-completion-close';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
        });
        modal.appendChild(closeBtn);

        // Header with Bluux branding
        const header = document.createElement('div');
        header.className = 'activity-completion-header';
        const logo = document.createElement('img');
        logo.src = 'assets/lobby/scratchJrlogo.svg';
        logo.alt = 'Bluux';
        logo.className = 'activity-completion-logo';
        header.appendChild(logo);
        const title = document.createElement('h3');
        title.className = 'activity-completion-title';
        title.textContent = 'Activity Complete!';
        header.appendChild(title);
        const subtitle = document.createElement('p');
        subtitle.className = 'activity-completion-subtitle';
        subtitle.textContent = 'My First Story Walk';
        header.appendChild(subtitle);
        modal.appendChild(header);

        // Body
        const body = document.createElement('div');
        body.className = 'activity-completion-body';

        const stars = document.createElement('div');
        stars.className = 'activity-completion-stars';
        stars.textContent = '\u2B50\u2B50\u2B50';
        body.appendChild(stars);

        const message = document.createElement('p');
        message.className = 'activity-completion-message';
        message.textContent = "You're a programmer now! You learned to tell Bluux where to go using blocks.";
        body.appendChild(message);

        const actions = document.createElement('div');
        actions.className = 'activity-completion-actions';

        const replayBtn = document.createElement('button');
        replayBtn.className = 'activity-completion-btn-replay';
        replayBtn.setAttribute('aria-label', 'Replay activity');
        replayBtn.innerHTML = '&#8634; Replay';
        replayBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
            this.restartActivity();
        });
        actions.appendChild(replayBtn);

        const nextBtn = document.createElement('button');
        nextBtn.className = 'activity-completion-btn-next';
        nextBtn.setAttribute('aria-label', 'Next activity');
        nextBtn.innerHTML = 'Next Activity &#9654;';
        nextBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
            // Navigate to hub/next activity — same as onNext callback
            ScratchJr.saveProject(null, ScratchJr.flippage);
        });
        actions.appendChild(nextBtn);

        body.appendChild(actions);
        modal.appendChild(body);
        this._completionOverlay.appendChild(modal);
        document.body.appendChild(this._completionOverlay);
    }

    _dismissCompletionModal () {
        if (this._completionOverlay) {
            this._completionOverlay.style.opacity = '0';
            setTimeout(() => {
                if (this._completionOverlay && this._completionOverlay.parentNode) {
                    this._completionOverlay.parentNode.removeChild(this._completionOverlay);
                }
                this._completionOverlay = null;
            }, 300);
        }
    }

    _setPhase (phase, opts = {}) {
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
        this._phase = phase;
        this._phaseStartedAt = Date.now();
        this._syncVisualState();
        this._stabilizeChildFacingUi();
        if (!opts.silent) {
            this._persistProgress();
        }
    }

    _syncVisualState () {
        if (this._paletteRestrictor) {
            const allowed = this._getAllowedBlocksForPhase();
            this._paletteRestrictor.updateAllowed(allowed);
        }

        if (this._pond) {
            const showPond = this._phase === PHASES.OBSTACLE_INTRO || this._phase === PHASES.TASK_3 || this._phase === PHASES.CELEBRATION || this._phase === PHASES.BONUS || this._phase === PHASES.COMPLETE;
            this._pond.classList.toggle('is-hidden', !showPond);
        }

        if (this._goalSparkles) {
            this._goalSparkles.style.left = `${this._config.goal.x - 34}px`;
            this._goalSparkles.style.top = `${this._config.goal.y - 34}px`;
        }

        if (this._phase === PHASES.INTRO || (this._phase === PHASES.TASK_1 && !this._scriptHas('onflag'))) {
            this._selectPaletteCategory(0);
        } else if (
            (this._phase === PHASES.TASK_1 || this._phase === PHASES.TASK_2) &&
            this._scriptHas('onflag') &&
            !this._scriptHas('forward')
        ) {
            this._selectPaletteCategory(1);
        }

        this._forcePlayerSelection();
        if (Thumbs && typeof Thumbs.updateSprites === 'function') {
            Thumbs.updateSprites();
        }
        this._attachReplayListener();
    }

    _startRunMonitor () {
        this._stopRunMonitor();
        this._runMonitor = setInterval(() => {
            const player = this._getPlayerSpriteOwner();
            if (!player) {
                return;
            }

            // Gap 4 (G2): Clamp player position to stage boundaries during the run
            // Track off-screen BEFORE clamping so _validateRun can detect it
            if (player.xcoor < 10 || player.xcoor > STAGE_WIDTH - 10 || player.ycoor < 10 || player.ycoor > STAGE_HEIGHT - 10) {
                this._wentOffScreen = true;
            }
            const clampedX = Math.max(10, Math.min(STAGE_WIDTH - 10, player.xcoor));
            const clampedY = Math.max(10, Math.min(STAGE_HEIGHT - 10, player.ycoor));
            if (clampedX !== player.xcoor || clampedY !== player.ycoor) {
                player.setPos(clampedX, clampedY);
            }

            const pos = {x: player.xcoor, y: player.ycoor};
            if ((this._phase === PHASES.TASK_3 || this._phase === PHASES.BONUS) && this._intersectsPond(pos)) {
                this._collisionOnRun = true;
                ScratchJr.stopStrips();
                if (this._runStartPosition) {
                    player.setPos(this._runStartPosition.x, this._runStartPosition.y);
                }
            } else if ((this._phase === PHASES.TASK_3 || this._phase === PHASES.BONUS) && this._nearPondEdge(pos)) {
                // Visual warning when Bluux gets close to pond
                if (!this._pondWarningShown) {
                    this._pondWarningShown = true;
                    this._showPlayerBubble('Careful, the pond!', 1500);
                    if (this._pond) {
                        this._pond.classList.add('activity-storywalk-pond-warn');
                        setTimeout(() => {
                            if (this._pond) {
                                this._pond.classList.remove('activity-storywalk-pond-warn');
                            }
                        }, 1200);
                    }
                }
            }
        }, 80);
    }

    _stopRunMonitor () {
        if (this._runMonitor) {
            clearInterval(this._runMonitor);
            this._runMonitor = null;
        }
    }

    _startIdleScaffolding () {
        this._clearIdleTimers();

        if (this._phase === PHASES.INTRO || this._phase === PHASES.OBSTACLE_INTRO || this._phase === PHASES.CELEBRATION || this._phase === PHASES.COMPLETE) {
            return;
        }

        const thresholds = this._config.idleScaffolding[this._phase] || [];
        thresholds.forEach((delay, index) => {
            const tier = index + 1;
            this._idleTimers.push(setTimeout(() => {
                if (this._lastInteractionAt + delay > Date.now()) {
                    return;
                }
                if (tier === 4 && !this._idlePausedAt) {
                    this._idlePausedAt = Date.now();
                }
                this._deliverIdleScaffold(tier);
            }, delay));
        });
    }

    _clearIdleTimers () {
        while (this._idleTimers.length > 0) {
            clearTimeout(this._idleTimers.pop());
        }
    }

    _deliverIdleScaffold (tier) {
        this._awaitingHintResponse = true;
        this._hintEvents.push({
            phase: this._phase,
            tier,
            timestamp: new Date().toISOString()
        });

        if (tier === 1) {
            const voiceId = this._phase === PHASES.TASK_3 ? 'VO-09' : 'VO-02';
            this._speak(voiceId, this._buildTierOneHintPrompt());
            return;
        }

        if (tier === 2) {
            this._pulseHintTarget();
            return;
        }

        if (tier === 3) {
            this._highlightHintTarget();
            this._speak(this._phase === PHASES.TASK_3 ? 'VO-09' : 'VO-03', this._buildTierThreePrompt());
            return;
        }

        if (tier === 4) {
            const target = this._getHintTarget();
            if (target.kind === 'run') {
                this._highlightHintTarget();
                this._speak('VO-21', this._buildTaskPrompt(this._phase));
                return;
            }
            this._showGhostHand();
            return;
        }

        // Gap 3 (G7): Tier 5 — repeat ghost-hand demo one more time
        if (tier === 5) {
            this._showGhostHand();
            return;
        }

        // Gap 3 (G7): Tier 6 — gentle exit message after continued idle
        if (tier === 6) {
            this._speak('VO-GENTLE', "That's okay! You can try again anytime.");
        }
    }

    _pulseHintTarget () {
        this._clearHintVisuals();
        const target = this._getHintTarget();
        if (target.kind === 'run') {
            const runButton = this._findRunButton();
            if (runButton) {
                runButton.classList.add('activity-storywalk-pulse');
            }
            return;
        }

        this._selectPaletteCategory(target.categoryIndex);
        const selector = this._getCategorySelector(target.categoryIndex);
        if (selector) {
            selector.classList.add('activity-storywalk-pulse');
        }
    }

    _highlightHintTarget () {
        this._clearHintVisuals();
        const target = this._getHintTarget();
        if (target.kind === 'run') {
            const runButton = this._findRunButton();
            if (runButton) {
                runButton.classList.add('activity-storywalk-target');
            }
            return;
        }

        this._selectPaletteCategory(target.categoryIndex);
        const block = this._findPaletteBlock(target.blockType);
        if (block) {
            block.classList.add('activity-storywalk-target');
        }
    }

    _showGhostHand () {
        this._clearHintVisuals();
        const target = this._getHintTarget();
        this._selectPaletteCategory(target.categoryIndex);
        const block = this._findPaletteBlock(target.blockType);
        const scripts = gn('scripts');
        if (!block || !scripts) {
            return;
        }

        const blockRect = block.getBoundingClientRect();
        const scriptRect = scripts.getBoundingClientRect();

        this._ghostHand = document.createElement('div');
        this._ghostHand.className = 'activity-storywalk-ghost-hand';
        this._ghostHand.style.left = `${blockRect.left + blockRect.width / 2}px`;
        this._ghostHand.style.top = `${blockRect.top + blockRect.height / 2}px`;
        this._ghostHand.style.setProperty('--activity-ghost-dx', `${scriptRect.left + 120 - (blockRect.left + blockRect.width / 2)}px`);
        this._ghostHand.style.setProperty('--activity-ghost-dy', `${scriptRect.top + 56 - (blockRect.top + blockRect.height / 2)}px`);
        document.body.appendChild(this._ghostHand);

        setTimeout(() => {
            if (this._ghostHand && this._ghostHand.parentNode) {
                this._ghostHand.parentNode.removeChild(this._ghostHand);
                this._ghostHand = null;
            }
        }, 1800);
    }

    _clearHintVisuals () {
        const root = (this._container && this._container.ownerDocument) || document;
        root.querySelectorAll('.activity-storywalk-pulse').forEach((el) => el.classList.remove('activity-storywalk-pulse'));
        root.querySelectorAll('.activity-storywalk-target').forEach((el) => el.classList.remove('activity-storywalk-target'));
        if (this._ghostHand && this._ghostHand.parentNode) {
            this._ghostHand.parentNode.removeChild(this._ghostHand);
            this._ghostHand = null;
        }
    }

    _showDoneButton () {
        if (this._doneBtn) {
            return;
        }

        let clicked = false;
        this._doneBtn = document.createElement('button');
        this._doneBtn.className = 'activity-storywalk-done-btn';
        this._doneBtn.type = 'button';
        this._doneBtn.setAttribute('aria-label', 'Done');
        this._doneBtn.textContent = 'Done';
        this._doneBtn.addEventListener('pointerup', () => {
            if (clicked) return;
            clicked = true;
            this._doneBtn.disabled = true;
            this._showPlayerBubble("I'm a programmer!", 4000);
            this._speak('VO-12');
            setTimeout(() => this._completeBonus(), 400);
        });
        this._container.appendChild(this._doneBtn);
    }

    _hideDoneButton () {
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        this._doneBtn = null;
    }

    _revealPond () {
        if (this._pond) {
            this._pond.classList.remove('is-hidden');
            this._pond.classList.add('is-revealed');
        }
    }

    _repositionSpritesAroundPond () {
        const pond = this._config.pond;
        const player = this._getPlayerSpriteOwner();
        const goal = this._getGoalSpriteOwner();

        if (player) {
            const playerX = Math.max(24, pond.x - 60);
            const playerY = pond.y + (pond.height / 2);
            player.setPos(playerX, playerY);
        }

        if (goal) {
            const goalX = Math.min(STAGE_WIDTH - 24, pond.x + pond.width + 60);
            const goalY = pond.y + (pond.height / 2);
            goal.setPos(goalX, goalY);
            // Sync goal criteria so _evaluateGoalFit uses the new position
            this._config.goal.x = goalX;
            this._config.goal.y = goalY;
            if (this._goalMarker) {
                this._goalMarker.updatePosition(goalX, goalY);
            }
        }
    }

    _faceGoalTowardPlayer () {
        const goal = this._getGoalSpriteOwner();
        const player = this._getPlayerSpriteOwner();
        if (!goal || !player) {
            return;
        }
        // Flip Dog to face left (toward player) if player is to its left
        if (player.xcoor < goal.xcoor) {
            goal.flip = true;
            goal.render();
        }
    }

    _getGoalSpriteOwner () {
        const spriteIds = ScratchJr.stage && ScratchJr.stage.currentPage && ScratchJr.stage.currentPage.getSprites
            ? ScratchJr.stage.currentPage.getSprites()
            : [];
        const spriteId = spriteIds[this._config.goalIndex] || spriteIds[1];
        const spriteEl = spriteId ? gn(spriteId) : null;
        return spriteEl && spriteEl.owner ? spriteEl.owner : null;
    }

    _launchCelebration () {
        ActivityAudio.play(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'star-chime'
            : 'confetti');
    }

    _showPlayerBubble (text, durationMs) {
        const player = this._getPlayerSpriteOwner();
        if (!player || typeof player.openBalloon !== 'function') {
            return;
        }
        player.openBalloon(text);
        if (durationMs && durationMs > 0) {
            setTimeout(() => {
                if (player && typeof player.closeBalloon === 'function') {
                    player.closeBalloon();
                }
            }, durationMs);
        }
    }

    _closePlayerBubble () {
        const player = this._getPlayerSpriteOwner();
        if (player && typeof player.closeBalloon === 'function') {
            player.closeBalloon();
        }
    }

    _showGoalBubble (text, durationMs) {
        const goal = this._getGoalSpriteOwner();
        if (!goal || typeof goal.openBalloon !== 'function') {
            return;
        }
        goal.openBalloon(text);
        if (durationMs && durationMs > 0) {
            setTimeout(() => {
                if (goal && typeof goal.closeBalloon === 'function') {
                    goal.closeBalloon();
                }
            }, durationMs);
        }
    }

    _celebrateMini () {
        this._showPlayerBubble('Great job!', 2500);
        ActivityAudio.play('star-chime');
        const player = this._getPlayerSpriteOwner();
        if (!player) {
            return;
        }

        const y = player.ycoor;
        player.setPos(player.xcoor, y - 20);
        setTimeout(() => player.setPos(player.xcoor, y), 250);
    }

    _showToast (kind) {
        if (this._toast && this._toast.parentNode) {
            this._toast.parentNode.removeChild(this._toast);
        }

        this._toast = document.createElement('div');
        this._toast.className = `activity-storywalk-toast activity-storywalk-toast-${kind}`;

        const labels = {
            splash: 'Splash!',
            celebrate: 'You did it!',
            mini: 'Great job!'
        };
        this._toast.textContent = labels[kind] || '';
        this._overlay.appendChild(this._toast);

        setTimeout(() => {
            if (this._toast && this._toast.parentNode) {
                this._toast.parentNode.removeChild(this._toast);
                this._toast = null;
            }
        }, 2200);
    }

    _intersectsPond (position) {
        const pond = this._config.pond;
        return (
            position.x >= pond.x &&
            position.x <= pond.x + pond.width &&
            position.y >= pond.y &&
            position.y <= pond.y + pond.height
        );
    }

    _nearPondEdge (position) {
        const pond = this._config.pond;
        const margin = 20;
        const nearX = position.x >= pond.x - margin && position.x <= pond.x + pond.width + margin;
        const nearY = position.y >= pond.y - margin && position.y <= pond.y + pond.height + margin;
        return nearX && nearY && !this._intersectsPond(position);
    }

    _getPlayerPosition () {
        const player = this._getPlayerSpriteOwner();
        if (!player) {
            return {x: 0, y: 0, w: 48, h: 48};
        }
        return {x: player.xcoor, y: player.ycoor, w: player.w || 48, h: player.h || 48};
    }

    _getPlayerSpriteSnapshot () {
        const player = this._getPlayerSpriteOwner();
        if (!player) {
            return {x: 0, y: 0, homeX: 0, homeY: 0, w: 32, h: 32, scale: 1};
        }

        return {
            x: player.xcoor,
            y: player.ycoor,
            homeX: player.homex,
            homeY: player.homey,
            w: player.w,
            h: player.h,
            scale: player.scale,
            role: player.role || 'player'
        };
    }

    _getPlayerSpriteOwner () {
        const spriteIds = ScratchJr.stage && ScratchJr.stage.currentPage && ScratchJr.stage.currentPage.getSprites
            ? ScratchJr.stage.currentPage.getSprites()
            : [];
        const preferredSpriteId = spriteIds.find((spriteId) => {
            const spriteEl = spriteId ? gn(spriteId) : null;
            const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
            return sprite && sprite.role === 'player';
        });
        const spriteId = preferredSpriteId || spriteIds[this._config.playerIndex] || spriteIds[0];
        const spriteEl = spriteId ? gn(spriteId) : null;
        return spriteEl && spriteEl.owner ? spriteEl.owner : null;
    }

    _applySpriteRoles () {
        const spriteIds = ScratchJr.stage && ScratchJr.stage.currentPage && ScratchJr.stage.currentPage.getSprites
            ? ScratchJr.stage.currentPage.getSprites()
            : [];
        if (!spriteIds || spriteIds.length < 1) {
            return false;
        }
        spriteIds.forEach((spriteId, index) => {
            const spriteEl = spriteId ? gn(spriteId) : null;
            const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
            if (!sprite) {
                return;
            }
            if (index === this._config.playerIndex) {
                sprite.role = 'player';
                return;
            }
            if (index === this._config.goalIndex) {
                sprite.role = 'goal';
                return;
            }
            sprite.role = sprite.role || 'npc';
        });
        return true;
    }

    _forcePlayerSelection () {
        const player = this._getPlayerSpriteOwner();
        const page = ScratchJr.stage && ScratchJr.stage.currentPage ? ScratchJr.stage.currentPage : null;
        if (!page) {
            return false;
        }

        if (!player) {
            const spriteIds = typeof page.getSprites === 'function' ? page.getSprites() : [];
            const fallbackPlayerId = spriteIds[this._config.playerIndex] || spriteIds[0] || '';
            if (fallbackPlayerId) {
                page.currentSpriteName = fallbackPlayerId;
                page.lastSprite = fallbackPlayerId;
                this._pruneSpriteRail(fallbackPlayerId);
                return true;
            }
            return false;
        }

        page.currentSpriteName = player.id;
        page.lastSprite = player.id;
        page.setCurrentSprite(player);
        if (Thumbs && typeof Thumbs.updateSprites === 'function') {
            Thumbs.updateSprites();
        }
        this._pruneSpriteRail(player.id);
        return true;
    }

    _getPlayerSpriteElement () {
        const player = this._getPlayerSpriteOwner();
        return player ? player.div : null;
    }

    _getCategorySelector (index) {
        const selectors = document.getElementById('selectors');
        if (!selectors) {
            return null;
        }
        return Array.from(selectors.childNodes || []).find((child) => child && child.index === index) || null;
    }

    _pruneSpriteRail (playerId) {
        const rail = document.getElementById('spritecc');
        if (!rail || !playerId) {
            return;
        }

        Array.from(rail.childNodes || []).forEach((thumb) => {
            if (!thumb || !thumb.owner) {
                return;
            }
            thumb.style.display = thumb.owner === playerId ? '' : 'none';
        });
    }

    _selectPaletteCategory (index) {
        if (typeof index !== 'number') {
            return;
        }
        this._lastPaletteCategory = index;
        try {
            Palette.selectCategory(index);
            this._lastPaletteCategory = typeof Palette.numcat === 'number' ? Palette.numcat : index;
        } catch (_) {
            this._scheduleUiRepair(120);
        }
    }

    _scheduleUiRepair (delayMs = 0) {
        this._clearUiRepair();
        this._uiRepairTimer = setTimeout(() => {
            this._uiRepairTimer = null;
            this._repairChildFacingUi();
        }, delayMs);
    }

    _clearUiRepair () {
        if (this._uiRepairTimer) {
            clearTimeout(this._uiRepairTimer);
            this._uiRepairTimer = null;
        }
    }

    _stabilizeChildFacingUi () {
        this._clearUiRepair();
        this._clearUiRepairs();
        [0, 300, 900, 1800, 3200].forEach((delayMs) => {
            const timer = setTimeout(() => {
                this._uiRepairTimers = this._uiRepairTimers.filter((activeTimer) => activeTimer !== timer);
                this._repairChildFacingUi();
            }, delayMs);
            this._uiRepairTimers.push(timer);
        });
    }

    _clearUiRepairs () {
        while (this._uiRepairTimers.length > 0) {
            clearTimeout(this._uiRepairTimers.pop());
        }
    }

    _repairChildFacingUi (attempt = 0) {
        const appliedRoles = this._applySpriteRoles();
        const hasPlayer = this._forcePlayerSelection();
        const targetPalette = this._getPreferredPaletteCategory();
        let paletteReady = true;

        if (typeof targetPalette === 'number') {
            this._selectPaletteCategory(targetPalette);
            paletteReady = typeof Palette !== 'undefined' && typeof Palette.numcat === 'number'
                ? Palette.numcat === targetPalette
                : this._lastPaletteCategory === targetPalette;
        }

        if ((appliedRoles === false || hasPlayer === false || paletteReady === false) && attempt < 6) {
            this._uiRepairTimer = setTimeout(() => {
                this._uiRepairTimer = null;
                this._repairChildFacingUi(attempt + 1);
            }, 140);
        }
    }

    _getPreferredPaletteCategory () {
        if (this._phase === PHASES.INTRO) {
            return 0;
        }

        if (this._phase === PHASES.TASK_1 && !this._scriptHas('onflag')) {
            return 0;
        }

        if ((this._phase === PHASES.TASK_1 || this._phase === PHASES.TASK_2) &&
            this._scriptHas('onflag') &&
            !this._scriptHas('forward')) {
            return 1;
        }

        if (this._phase === PHASES.TASK_3 || this._phase === PHASES.CELEBRATION || this._phase === PHASES.BONUS) {
            return 1;
        }

        return this._lastPaletteCategory;
    }

    _getAllowedBlocksForPhase () {
        if (this._phase === PHASES.INTRO) {
            return ['onflag'];
        }

        if (this._phase === PHASES.TASK_1 && !this._scriptHas('onflag')) {
            return ['onflag'];
        }

        if (this._phase === PHASES.TASK_1 && this._scriptHas('onflag') && !this._scriptHas('forward')) {
            return ['onflag', 'forward'];
        }

        return this._config.availableBlocks[this._phase] || [];
    }

    _evaluateGoalFit (sprite) {
        const criteria = {
            goalX: this._config.goal.x,
            goalY: this._config.goal.y,
            tolerance: this._config.goal.tolerance,
            goalCoverageThreshold: this._config.goal.coverageThreshold,
            goalHitboxScale: this._config.goal.hitboxScale
        };

        if (ScratchJr && typeof ScratchJr.evaluateGoalFit === 'function') {
            return ScratchJr.evaluateGoalFit(sprite, criteria);
        }

        if (!sprite) {
            return {
                success: false,
                distance: Infinity,
                coverage: 0,
                tolerance: criteria.tolerance,
                requiredCoverage: criteria.goalCoverageThreshold
            };
        }

        const dx = sprite.x - criteria.goalX;
        const dy = sprite.y - criteria.goalY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const halfWidth = Math.max(8, ((sprite.w || 32) * (sprite.scale || 1)) / 2);
        const halfHeight = Math.max(8, ((sprite.h || 32) * (sprite.scale || 1)) / 2);
        const sampleCols = 7;
        const sampleRows = 7;
        let insideCount = 0;
        let totalCount = 0;

        for (let row = 0; row < sampleRows; row++) {
            const sampleY = sprite.y - halfHeight + (((row + 0.5) / sampleRows) * halfHeight * 2);
            for (let col = 0; col < sampleCols; col++) {
                const sampleX = sprite.x - halfWidth + (((col + 0.5) / sampleCols) * halfWidth * 2);
                totalCount++;
                const sampleDx = sampleX - criteria.goalX;
                const sampleDy = sampleY - criteria.goalY;
                if (Math.sqrt(sampleDx * sampleDx + sampleDy * sampleDy) <= criteria.tolerance) {
                    insideCount++;
                }
            }
        }

        const coverage = totalCount > 0 ? (insideCount / totalCount) : 0;

        return {
            success: coverage >= criteria.goalCoverageThreshold,
            distance,
            coverage,
            tolerance: criteria.tolerance,
            requiredCoverage: criteria.goalCoverageThreshold
        };
    }

    _findRunButton () {
        return document.getElementById('go');
    }

    _findPaletteBlock (blockType) {
        const palette = document.getElementById('palette');
        if (!palette) {
            return null;
        }
        return Array.from(palette.childNodes || []).find((child) => child && child.owner && child.owner.blocktype === blockType) || null;
    }

    _scriptHas (blockType) {
        return ScratchJr.collectActivityBlocks().some((block) => block && block.blocktype === blockType);
    }

    _getBlockSignature () {
        return ScratchJr.collectActivityBlocks().map((block) => {
            const argValue = block && block.arg && typeof block.arg.argValue !== 'undefined'
                ? block.arg.argValue
                : (block && typeof block.getArgValue === 'function' ? block.getArgValue() : '');
            return `${block.blocktype}:${argValue}`;
        }).join('|');
    }

    async _buildSkillProfile (savedProgress, savedPhase) {
        const progress = savedProgress || {};
        const allProgress = await ActivityStorageService.getProgress();
        const completedCount = Object.values(allProgress || {}).filter((entry) => entry && entry.status === 'completed').length;
        const hintCount = Array.isArray(progress.hintEvents)
            ? progress.hintEvents.length
            : Number(progress.hintsUsed || 0);
        const outcomes = progress.outcomeFlags || {};

        let level = 'newcomer';
        if (progress.status === 'completed' || completedCount >= 2) {
            level = 'confident';
        } else if (
            outcomes.outcome_1 ||
            outcomes.outcome_2 ||
            savedPhase === PHASES.TASK_2 ||
            savedPhase === PHASES.TASK_3 ||
            (progress.scriptSnapshot && String(progress.scriptSnapshot).length > 0)
        ) {
            level = hintCount >= 4 ? 'supported' : 'growing';
        }

        return {
            level,
            completedCount,
            hintCount,
            isReplay: progress.status === 'completed',
            savedPhase: savedPhase || null
        };
    }

    _buildIntroVoiceLine () {
        const phase = this._config.introAdvancePhase || PHASES.TASK_1;
        const skillLevel = this._skillProfile && this._skillProfile.level ? this._skillProfile.level : 'newcomer';
        const spokenName = 'Blooks';

        // Part 1 ("Hi! I'm Bluux!") is shown as speech bubble and spoken separately.
        // This method returns Part 2 — the continuation about Dog and what to do.

        if (phase === PHASES.TASK_3) {
            if (skillLevel === 'confident') {
                return `${spokenName} needs your help getting around the pond to Dog. Plan your order with the green flag, then blue move blocks.`;
            }
            if (skillLevel === 'growing') {
                return `${spokenName} needs to get around the pond and reach Dog. Start with the green flag, then try going up or down before you go right.`;
            }
            return `${spokenName} needs to get around the pond and reach Dog. Start with the green flag. Then try a blue up or down block before you go right.`;
        }

        if (skillLevel === 'confident') {
            return "That's my friend Dog over there. Dog is just two moves away. Tap the yellow start blocks and find the green flag.";
        }
        if (skillLevel === 'growing') {
            return "That's my friend Dog over there. Can you help me walk to Dog? Tap the yellow start blocks and find the green flag.";
        }
        return "That's my friend Dog over there. Can you help me walk to Dog? Use these blocks to tell me where to go!";
    }

    _buildTaskPrompt (phase, opts = {}) {
        const skillLevel = this._skillProfile && this._skillProfile.level ? this._skillProfile.level : 'newcomer';
        const isResume = Boolean(opts.isResume);

        if (phase === PHASES.TASK_3) {
            if (skillLevel === 'confident') {
                return `${isResume ? "You're back." : 'Nice work.'} Help Blooks get around the pond and reach Dog. Think about which direction should come first.`;
            }
            if (skillLevel === 'growing') {
                return `${isResume ? "You're back." : 'Keep going.'} Help Blooks get around the pond to Dog. Try moving up or down before you move right.`;
            }
            return `${isResume ? "You're back." : 'Can you help me?'} Help Blooks get around the pond to Dog. Start with the green flag, then try an up or down block before you go right.`;
        }

        if (phase === PHASES.TASK_2) {
            return 'Nice work. Press the green flag again to help Blooks move closer to Dog.';
        }

        return 'Start with the green flag. Drag it down here.';
    }

    _buildRunPrompt () {
        if (this._phase === PHASES.TASK_2) {
            return 'Nice work. Press the green flag again to help Blooks move closer to Dog.';
        }
        return 'Great job! Now press the green flag at the top to see Blooks move.';
    }

    _buildTierOneHintPrompt () {
        if (this._phase === PHASES.TASK_3) {
            return 'Blooks needs to go around the pond. Which direction should come first: up, down, or right?';
        }

        if (!this._scriptHas('onflag')) {
            return 'Start with the green flag. Drag it down here.';
        }

        if (!this._scriptHas('forward')) {
            return 'Now tap the blue blocks. Find the right arrow block and connect it to the green flag.';
        }

        if (!this._scriptHas('endstack')) {
            return 'Almost there! Every program needs an ending. Find the red end block and connect it at the end. It tells Blooks when to stop.';
        }

        return this._buildRunPrompt();
    }

    _buildTierThreePrompt () {
        const target = this._getHintTarget();
        if (target.kind === 'run') {
            return 'Press the green flag at the top.';
        }
        if (target.blockType === 'onflag') {
            return 'Try this yellow flag block first.';
        }
        if (target.blockType === 'endstack') {
            return 'Try this red end block! It goes at the end to tell Blooks when to stop.';
        }
        if (target.blockType === 'up') {
            return 'Try this blue up block to go around the pond.';
        }
        if (target.blockType === 'down') {
            return 'Try this blue down block to go around the pond.';
        }
        return 'Try this blue right arrow block. Connect it to the green flag.';
    }

    _getHintTarget () {
        if (!this._scriptHas('onflag')) {
            return {kind: 'block', blockType: 'onflag', categoryIndex: 0};
        }

        if (this._phase === PHASES.TASK_1 && !this._scriptHas('forward')) {
            return {kind: 'block', blockType: 'forward', categoryIndex: 1};
        }

        if (this._phase === PHASES.TASK_1 && !this._scriptHas('endstack')) {
            return {kind: 'block', blockType: 'endstack', categoryIndex: 5};
        }

        if (this._phase === PHASES.TASK_1) {
            return {kind: 'run'};
        }

        // Task 2: guide child to say block (speech bubbles only)
        if (this._phase === PHASES.TASK_2) {
            if (!this._scriptHas('say')) {
                return {kind: 'block', blockType: 'say', categoryIndex: 2};
            }
            return {kind: 'run'};
        }

        if (this._phase === PHASES.TASK_3 || this._phase === PHASES.BONUS) {
            const hasVertical = this._scriptHas('up') || this._scriptHas('down');
            if (!hasVertical) {
                const player = this._getPlayerPosition();
                const pondMidY = this._config.pond.y + (this._config.pond.height / 2);
                return {
                    kind: 'block',
                    blockType: player.y >= pondMidY ? 'up' : 'down',
                    categoryIndex: 1
                };
            }
            if (!this._scriptHas('forward')) {
                return {kind: 'block', blockType: 'forward', categoryIndex: 1};
            }
            return {kind: 'block', blockType: this._scriptHas('up') ? 'forward' : 'up', categoryIndex: 1};
        }

        return {kind: 'block', blockType: 'forward', categoryIndex: 1};
    }

    _replayCurrentNarration () {
        if (this._lastNarrationId) {
            this._speak(this._lastNarrationId, this._lastNarrationText);
        }
    }

    _maybeCelebrateProgress (previousSignature, nextSignature) {
        if (!nextSignature) {
            return;
        }

        const previousTypes = this._extractBlockTypes(previousSignature);
        const nextTypes = this._extractBlockTypes(nextSignature);
        const nextCounts = this._countBlockTypes(nextTypes);

        // Flag placed — delegate to _advanceToTask1 for progressive coaching
        if (this._phase === PHASES.TASK_1 &&
            !this._milestoneAnnouncements.flagPlaced &&
            nextTypes.includes('onflag')) {
            this._advanceToTask1();
            return;
        }

        // Move block placed — delegate to _advanceToTask1 for end block guidance
        if (this._phase === PHASES.TASK_1 &&
            !this._milestoneAnnouncements.movePlaced &&
            nextTypes.includes('forward') &&
            !previousTypes.includes('forward')) {
            this._advanceToTask1();
            return;
        }

        // End block placed — delegate to _advanceToTask1 for run guidance
        if (this._phase === PHASES.TASK_1 &&
            !this._milestoneAnnouncements.endPlaced &&
            nextTypes.includes('endstack') &&
            !previousTypes.includes('endstack')) {
            this._advanceToTask1();
            return;
        }

        if ((this._phase === PHASES.TASK_3 || this._phase === PHASES.BONUS) &&
            !this._milestoneAnnouncements.verticalChoice &&
            (nextTypes.includes('up') || nextTypes.includes('down')) &&
            !(previousTypes.includes('up') || previousTypes.includes('down'))) {
            this._announceCoach('verticalChoice', 'VO-18');
            return;
        }

        if ((this._phase === PHASES.TASK_2 || this._phase === PHASES.TASK_3) &&
            !this._milestoneAnnouncements.sequenceBuilt &&
            (nextCounts.forward || 0) >= 2) {
            this._announceCoach('sequenceBuilt', 'VO-15');
            return;
        }

        if ((this._phase === PHASES.TASK_3 || this._phase === PHASES.BONUS) &&
            this._hasScriptChanged &&
            !this._milestoneAnnouncements.debugging &&
            previousSignature &&
            previousSignature.length > 0) {
            this._announceCoach('debugging', 'VO-17');
            return;
        }

        if (this._awaitingHintResponse && this._announceCoach('hintResponse', 'VO-16')) {
            this._awaitingHintResponse = false;
        }
    }

    _announceCoach (key, voiceId, opts = {}) {
        if (this._milestoneAnnouncements[key]) {
            return false;
        }

        this._milestoneAnnouncements[key] = true;
        this._lastCoachAt = Date.now();
        this._awaitingHintResponse = false;

        const afterSpeak = typeof opts.afterSpeak === 'function' ? opts.afterSpeak : null;
        const afterDelay = opts.afterMs || 0;
        const narrationText = this._config.voiceOver[voiceId] || this._defaultVoiceLine(voiceId);
        let finished = false;
        const runAfterSpeak = () => {
            if (finished || !afterSpeak) {
                return;
            }
            finished = true;
            if (afterDelay > 0) {
                this._scheduleFollowUp(afterDelay, afterSpeak);
            } else {
                afterSpeak();
            }
        };

        this._speak(voiceId, null, {
            onend: runAfterSpeak
        });

        if (afterSpeak) {
            this._scheduleFollowUp(this._estimateSpeechDurationMs(narrationText) + afterDelay + 250, runAfterSpeak);
        }

        return true;
    }

    _speakFunny (id, fallbackText) {
        const text = fallbackText || this._config.voiceOver[id] || this._defaultVoiceLine(id);
        if (!text || !window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
            return;
        }
        try {
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(text);
            const preferredVoice = this._getPreferredVoice();
            utterance.lang = preferredVoice && preferredVoice.lang ? preferredVoice.lang : 'en-US';
            if (preferredVoice) {
                utterance.voice = preferredVoice;
            }
            // Funny penguin voice — higher pitch, slightly faster
            utterance.rate = 1.1;
            utterance.pitch = 1.6;
            utterance.volume = 1;
            window.speechSynthesis.speak(utterance);
        } catch (_) {
            // Speech synthesis not available
        }
    }

    _extractBlockTypes (signature) {
        if (!signature) {
            return [];
        }
        return String(signature)
            .split('|')
            .filter(Boolean)
            .map((part) => String(part).split(':')[0]);
    }

    _countBlockTypes (types) {
        return types.reduce((counts, type) => {
            counts[type] = (counts[type] || 0) + 1;
            return counts;
        }, {});
    }

    _speak (id, fallbackText, opts = {}) {
        const text = fallbackText || this._config.voiceOver[id] || this._defaultVoiceLine(id);
        if (!text) {
            return;
        }

        const interruptIds = ['VO-08', 'VO-20'];
        const shouldInterrupt = Boolean(opts.interrupt) || interruptIds.includes(id);

        if (this._isSpeaking && !shouldInterrupt) {
            this._speechQueue.push({id, text, opts});
            return;
        }

        if (shouldInterrupt) {
            this._speechQueue = [];
            if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
                window.speechSynthesis.cancel();
            }
            this._isSpeaking = false;
        }

        this._dispatchSpeech(id, text, opts);
    }

    _dispatchSpeech (id, text, opts = {}) {
        this._lastNarrationId = id;
        this._lastNarrationText = text;
        this._lastSpokenAt = Date.now();
        this._isSpeaking = true;
        if (!this._speechVoice) {
            this._refreshPreferredVoice();
        }
        const preferredVoice = this._getPreferredVoice();
        this._lastVoiceName = preferredVoice && preferredVoice.name ? preferredVoice.name : '';
        this._lastVoiceLang = preferredVoice && preferredVoice.lang ? preferredVoice.lang : 'en-US';
        const entry = {
            id,
            text,
            phase: this._phase,
            voiceName: this._lastVoiceName,
            voiceLang: this._lastVoiceLang,
            voiceScore: this._speechVoiceScore,
            skillLevel: this._skillProfile && this._skillProfile.level ? this._skillProfile.level : 'newcomer',
            spokenAt: new Date().toISOString(),
            spokenAtMs: Date.now(),
            endedAt: null,
            endedAtMs: null
        };
        this._voiceHistory.push(entry);
        this._voiceHistory = this._voiceHistory.slice(-20);

        const finish = () => {
            if (entry.endedAtMs) {
                return;
            }
            entry.endedAt = new Date().toISOString();
            entry.endedAtMs = Date.now();
            this._isSpeaking = false;
            if (typeof opts.onend === 'function') {
                opts.onend(entry);
            }
            this._drainSpeechQueue();
        };

        if (window.speechSynthesis && typeof SpeechSynthesisUtterance !== 'undefined') {
            try {
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.onend = finish;
                utterance.onerror = finish;
                utterance.lang = preferredVoice && preferredVoice.lang ? preferredVoice.lang : 'en-US';
                if (preferredVoice) {
                    utterance.voice = preferredVoice;
                }
                utterance.rate = 0.88;
                utterance.pitch = 1.18;
                utterance.volume = 1;
                window.speechSynthesis.speak(utterance);
            } catch (_) {
                setTimeout(finish, this._estimateSpeechDurationMs(text));
            }
        } else {
            setTimeout(finish, this._estimateSpeechDurationMs(text));
        }
    }

    _drainSpeechQueue () {
        if (this._isSpeaking) {
            return;
        }
        const next = this._speechQueue.shift();
        if (next) {
            this._dispatchSpeech(next.id, next.text, next.opts);
        }
    }

    _playIntroSequence () {
        let advanced = false;

        const advanceFromIntro = () => {
            if (advanced || this._phase !== PHASES.INTRO) {
                return;
            }
            advanced = true;
            if (this._phaseTimeout) {
                clearTimeout(this._phaseTimeout);
                this._phaseTimeout = null;
            }
            const player = this._getPlayerSpriteOwner();
            if (player) {
                player.closeBalloon();
            }
            this._advanceFromIntro();
        };

        // Part 1: Show speech bubble "Hi! I'm Bluux!" and speak it
        const player = this._getPlayerSpriteOwner();
        const greetingBubble = "Hi! I'm Bluux!";
        const greetingSpoken = "Hi! I'm Blooks!";
        if (player && typeof player.openBalloon === 'function') {
            player.openBalloon(greetingBubble);
        }
        this._speak('VO-INTRO-1', greetingSpoken, {
            onend: () => {
                if (advanced || this._phase !== PHASES.INTRO) {
                    return;
                }
                // Part 2: Close first bubble, show second, speak continuation
                if (player && typeof player.closeBalloon === 'function') {
                    player.closeBalloon();
                }
                const continuationText = this._buildIntroVoiceLine();
                if (player && typeof player.openBalloon === 'function') {
                    player.openBalloon("That's my friend Dog over there!");
                }
                this._speak('VO-01', continuationText, {
                    onend: () => {
                        if (advanced || this._phase !== PHASES.INTRO) {
                            return;
                        }
                        if (player && typeof player.closeBalloon === 'function') {
                            player.closeBalloon();
                        }
                        if (this._phaseTimeout) {
                            clearTimeout(this._phaseTimeout);
                        }
                        this._phaseTimeout = setTimeout(advanceFromIntro, this._config.introPauseMs);
                    }
                });
            }
        });

        // Safety timeout in case speech callbacks don't fire
        const totalEstimatedMs = this._estimateSpeechDurationMs(greetingSpoken)
            + this._estimateSpeechDurationMs(this._buildIntroVoiceLine())
            + this._config.introPauseMs + 3000;
        this._phaseTimeout = setTimeout(advanceFromIntro, totalEstimatedMs);
    }

    _estimateSpeechDurationMs (text) {
        const words = String(text || '').trim().split(/\s+/).filter(Boolean).length;
        return Math.max(900, words * 260);
    }

    _defaultVoiceLine (id) {
        const map = {
            'VO-INTRO-1': "Hi! I'm Blooks!",
            'VO-01': "That's my friend Dog over there. Can you help me walk to Dog? Use these blocks to tell me where to go!",
            'VO-02': 'See the green flag? Every program needs a start. The green flag is like saying ready, set, go! Find the green flag and drag it down here.',
            'VO-03': 'Try this blue right arrow block. Connect it to the green flag.',
            'VO-04': 'I moved! Can you help me move even more?',
            'VO-05': "Wow, I'm getting closer! But Dog is still far away. Let's keep going!",
            'VO-06': "Oh no, there's a pond! I can't walk through water. Can you help me go around it?",
            'VO-07': 'Look, you have new blocks! These can help me move up and down too!',
            'VO-08': "Splash! That's the pond! Let me go back. Try a different way around.",
            'VO-09': 'I need to go around the pond. Which direction should I go first?',
            'VO-10': 'You did it! Blooks found Dog! You wrote a whole program to help Blooks walk across the park!',
            'VO-11': 'Great job! Now try the green sound block. You can even record your own voice!',
            'VO-12': "You're a programmer now! You learned to put blocks in order to help me move. That's called a sequence!",
            'VO-13': 'You found the green flag! When we press it, it tells Blooks to start moving. It is like pressing play on a movie. This is how we make programs!',
            'VO-14': 'Great job! You connected the blue arrow to the green flag. Now Blooks knows to move right when we press the flag.',
            'VO-END': 'Now we need to tell Blooks when to stop! Find the red end block. It is like the last page of a story. Drag it to the end of your blocks.',
            'VO-END-DONE': 'You did it! The red block means the end. Now your program has a beginning, a middle, and an end. Just like a story! That is a real program!',
            'VO-15': 'Yes! You are building a sequence.',
            'VO-16': 'Nice trying! That was a smart next step.',
            'VO-17': 'Great fixing! You changed your steps.',
            'VO-18': 'Yes! That direction helps Blooks go around the pond.',
            'VO-19': 'Now tap the blue blocks. Find the right arrow block and connect it to the green flag.',
            'VO-20': 'Now press the green flag to run your program and see Blooks move!',
            'VO-21': 'Nice work. Press the green flag again to help Blooks move closer to Dog.',
            'VO-TALK': 'Now make them talk! Try the purple say block to add a speech bubble.',
            'VO-OFFSCREEN': "Whoa, that's the wrong way! Dog is over there!",
            'VO-GENTLE': "That's okay! You can try again anytime."
        };
        return map[id] || '';
    }

    _initVoiceSelection () {
        if (!window.speechSynthesis) {
            return;
        }

        this._refreshPreferredVoice();
        if (!this._speechVoice && typeof window.speechSynthesis.addEventListener === 'function') {
            window.speechSynthesis.addEventListener('voiceschanged', this._voicesChangeHandler);
        }
    }

    _waitForPreferredVoice (timeoutMs = 5000) {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return Promise.resolve();
        }

        this._refreshPreferredVoice();
        if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let finished = false;
            let pollTimer = null;
            let timeoutId = null;

            const cleanup = () => {
                if (pollTimer) {
                    clearInterval(pollTimer);
                    pollTimer = null;
                }
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                if (typeof window.speechSynthesis.removeEventListener === 'function') {
                    window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
                }
            };

            const finish = () => {
                if (finished) {
                    return;
                }
                finished = true;
                cleanup();
                this._refreshPreferredVoice();
                resolve();
            };

            const handleVoicesChanged = () => {
                this._refreshPreferredVoice();
                if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
                    finish();
                }
            };

            if (typeof window.speechSynthesis.addEventListener === 'function') {
                window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
            }

            pollTimer = setInterval(() => {
                this._refreshPreferredVoice();
                if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
                    finish();
                }
            }, 100);

            timeoutId = setTimeout(finish, timeoutMs);
        });
    }

    _refreshPreferredVoice () {
        const selection = this._selectPreferredVoice();
        this._speechVoice = selection ? selection.voice : null;
        this._speechVoiceScore = selection ? selection.score : 0;
    }

    _getPreferredVoice () {
        if (!this._speechVoice) {
            this._refreshPreferredVoice();
        }
        return this._speechVoice;
    }

    _selectPreferredVoice () {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return null;
        }

        const voices = window.speechSynthesis.getVoices();
        if (!Array.isArray(voices) || voices.length < 1) {
            return null;
        }

        const ranked = voices
            .map((voice) => ({voice, score: this._scoreVoice(voice)}))
            .sort((a, b) => b.score - a.score);

        return ranked[0] && ranked[0].score > 0 ? ranked[0] : null;
    }

    _scoreVoice (voice) {
        return scorePreferredNarrationVoice(voice, window);
    }

    _resetForReplay () {
        this._clearIdleTimers();
        this._stopRunMonitor();
        this._stopScriptMonitor();
        this._clearCoachTimeout();
        this._clearInteractionSyncTimers();
        this._clearFollowUpTimers();
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
        this._speechQueue = [];
        this._isSpeaking = false;
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            window.speechSynthesis.cancel();
        }
        this._hideDoneButton();
        this._clearHintVisuals();
        this._collisionOnRun = false;
        this._wentOffScreen = false;
        this._pondWarningShown = false;
        this._outcomeFlags = {
            outcome_1: false,
            outcome_2: false,
            outcome_3: false,
            outcome_4: false,
            outcome_5: false
        };
        this._hintEvents = [];
        this._pondCollisions = 0;
        this._hasScriptChanged = false;
        this._lastBlockSignature = '';
        this._runStartPosition = null;
        this._lastNarrationId = null;
        this._lastNarrationText = '';
        this._voiceHistory = [];
        this._lastCoachAt = 0;
        this._lastPaletteCategory = null;
        this._awaitingHintResponse = false;
        this._milestoneAnnouncements = {
            flagPlaced: false,
            movePlaced: false,
            endPlaced: false,
            sequenceBuilt: false,
            verticalChoice: false,
            debugging: false,
            hintResponse: false
        };
        this._sessionStartedAt = Date.now();
        this._lastInteractionAt = Date.now();
        this._idlePausedAt = null;
        this._idleTimeMs = 0;
        ScratchJr.stopStrips();
        ScratchJr.resetSprites();
        this._clearPlayerScript();
        this._phase = this._config.restartPhase || PHASES.TASK_1;
        this._syncVisualState();
        this._startScriptMonitor();
        this._startIdleScaffolding();
        this._speak('VO-02', this._buildTaskPrompt(this._phase));
        this._persistProgress({
            status: 'in_progress',
            completedAt: null
        });
    }

    _clearPlayerScript () {
        const player = this._getPlayerSpriteOwner();
        const scriptsDiv = player ? gn(player.id + '_scripts') : null;
        if (!scriptsDiv || !scriptsDiv.owner || typeof scriptsDiv.owner.getBlocks !== 'function') {
            return;
        }

        scriptsDiv.owner.dragList = scriptsDiv.owner.getBlocks();
        scriptsDiv.owner.deleteBlocks();
        scriptsDiv.owner.dragList = [];
    }

    async _persistProgress (overrides = {}) {
        const position = this._getPlayerPosition();
        const now = Date.now();
        const elapsedMs = Math.max(0, now - this._sessionStartedAt - this._idleTimeMs);
        const status = overrides.status || (this._phase === PHASES.COMPLETE ? 'completed' : 'in_progress');

        await ActivityStorageService.saveProgress(this._activityData.activityId, {
            status,
            attempts: 0,
            stars: status === 'completed' ? 3 : 0,
            bestScore: status === 'completed' ? 3 : 0,
            hintsUsed: this._hintEvents.length,
            timeSpentMs: 0,
            currentState: this._phase,
            catPosition: position,
            scriptSnapshot: this._getBlockSignature(),
            totalActiveTimeSec: Math.round(elapsedMs / 1000),
            hintEvents: this._hintEvents.slice(),
            outcomeFlags: {...this._outcomeFlags},
            pondCollisions: this._pondCollisions,
            completedAt: overrides.completedAt,
            lastOpenedAt: new Date().toISOString(),
            sessionStartedAt: this._sessionStartedAt,
            idleTimeMs: this._idleTimeMs
        });
    }

    _scheduleCoachTimeout (delayMs, callback) {
        this._clearCoachTimeout();
        this._coachTimeout = setTimeout(() => {
            this._coachTimeout = null;
            callback();
        }, delayMs);
    }

    _clearCoachTimeout () {
        if (this._coachTimeout) {
            clearTimeout(this._coachTimeout);
            this._coachTimeout = null;
        }
    }

    shouldResetSpritesBeforeRun () {
        return !this._config.persistPositionBetweenRuns;
    }
}
